/*
 * CLAIM PREPARE client (#275, boardroom ruling A) — my-api POST
 * /account/claim/prepare {email}. For an UNCONFIRMED account the server sets a
 * fresh random permanent password (the account becomes CONFIRMED, so any
 * password set before the mailbox owner arrived stops working) and marks
 * email_verified=true; every other state is a strict no-op. The answer is an
 * identical 204 either way. The caller then requests ONE EMAIL_OTP — the
 * owner still proves mailbox control by entering it.
 *
 * FAIL CLOSED: anything but a 2xx throws, so the caller sends NO code:
 *   429 → AuthFlowError('TooManyRequests') ("Too many attempts…" copy);
 *   503 / other / network / no api base → AuthFlowError('Unknown') (the
 *   neutral "We couldn't send a code just now…" copy).
 * The email goes NFC+trimmed but CASE-PRESERVED, exactly like the probe, so
 * the server resolves the same (possibly mixed-case legacy) account.
 */
import { myApiBase } from '../data/myApi'
import { AuthFlowError } from './cognitoAuth'
import { parseRetryAfter } from './retryAfter'

export async function prepareUnconfirmedClaim(
  email: string,
  fetchImpl: typeof fetch = fetch,
  apiBase: string | null = myApiBase(),
): Promise<void> {
  if (!apiBase) throw new AuthFlowError('Unknown', 'claim prepare unavailable (no api base)')
  let res: Response
  try {
    res = await fetchImpl(`${apiBase.replace(/\/$/, '')}/account/claim/prepare`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.normalize('NFC').trim() }),
    })
  } catch {
    throw new AuthFlowError('Unknown', 'claim prepare unavailable (network)')
  }
  if (res.status === 429) {
    // Header first (exposed by my-api), else the body's retry_after.
    const body = (await res.json?.().catch(() => ({}))) as { retry_after?: unknown } | undefined
    const wait = parseRetryAfter(res.headers?.get?.('Retry-After'))
      ?? parseRetryAfter(typeof body?.retry_after === 'number' ? String(body.retry_after) : undefined)
    throw new AuthFlowError('TooManyRequests', 'rate limited', wait)
  }
  if (!res.ok) throw new AuthFlowError('Unknown', `claim prepare unavailable (${res.status})`)
}
